<template>
  <div>
    <div
      v-if="loading"
      class="page-loading d-flex flex-row align-items-center justify-content-center"
    >
      <i-spinner data-testid="document-page-loading" type="neutral"></i-spinner>
    </div>
    <div id="viewerContainer" ref="container">
      <div id="viewer" class="pdfViewer" />
    </div>
  </div>
</template>
<script>
'use strict';

import { getDocument } from 'pdfjs-dist';
import {
  PDFFindController,
  PDFLinkService,
  PDFPageView,
  EventBus,
} from 'pdfjs-dist/web/pdf_viewer';
import * as PDFJS from 'pdfjs-dist';
import { WorkerMessageHandler } from 'pdfjs-dist/build/pdf.worker.min';
import { iSpinner } from '@inscribe/signal';

function isPDFDocumentLoadingTask(obj) {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    obj.__PDFDocumentLoadingTask === true
  );
}

export function createLoadingTask(src, options) {
  var source;
  if (typeof src === 'string')
    source = {
      url: src,
    };
  else if (typeof src === 'object' && src !== null)
    source = Object.assign({}, src);
  else throw new TypeError('invalid src type');

  var loadingTask = getDocument({
    url: source.url,
    isEvalSupported: false,
    useWasm: false,
    wasmUrl: window.location.pathname + 'assets/pdfjs-dist/wasm/',
  }).promise;
  loadingTask.__PDFDocumentLoadingTask = true; // since PDFDocumentLoadingTask is not public

  if (options && options.onPassword)
    loadingTask.onPassword = options.onPassword;

  if (options && options.onProgress)
    loadingTask.onProgress = options.onProgress;

  return loadingTask;
}

export default {
  createLoadingTask: createLoadingTask,
  components: {
    iSpinner,
  },
  props: {
    src: {
      type: [String, Object, Promise],
      default: '',
    },
    page: {
      type: Number,
      default: 1,
    },
    rotate: {
      type: Number,
      default: 0,
    },
    scale: {
      type: [Number, String],
      default: 1,
    },
    text: {
      type: Boolean,
      default: true,
    },
  },
  data: function () {
    return {
      internalSrc: this.src,
      pdf: null,
      loading: true,
    };
  },
  watch: {
    pdf: function (val) {
      var pdfInfo = val.pdfInfo || val._pdfInfo;
      this.$emit('numpages', pdfInfo.numPages);
    },
    page: function (val) {
      var self = this;
      this.pdf.getPage(val).then(function (pdfPage) {
        self.pdfViewer.setPdfPage(pdfPage);
        self.pdfViewer.draw();
      });
    },
    scale: function (val) {
      this.drawScaled(val);
    },
    rotate: function (newRotate) {
      if (this.pdfViewer) {
        this.pdfViewer.update({
          scale: this.scale,
          rotation: newRotate,
        });
        this.pdfViewer.draw();
      }
    },
  },
  mounted: function () {
    var self = this;
    PDFJS.GlobalWorkerOptions.src = WorkerMessageHandler;
    if (!isPDFDocumentLoadingTask(self.internalSrc)) {
      self.internalSrc = createLoadingTask(self.internalSrc);
      self.$emit('loading', true);
    }

    var container = this.$refs.container;
    var eventBus = new EventBus();
    eventBus.on('pagerendered', function (e) {
      self.$emit('pagerendered', e);
    });

    // (Optionally) enable hyperlinks within PDF files.
    self.pdfLinkService = new PDFLinkService({
      eventBus: eventBus,
      externalLinkTarget: 2,
    });

    // (Optionally) enable find controller.
    self.pdfFindController = new PDFFindController({
      eventBus: eventBus,
      linkService: self.pdfLinkService,
    });

    let textLayer;
    if (self.text) {
      textLayer = {};
      // this is commented because it gives an error in latest version of PDFJS
      // textLayer = new DefaultTextLayerFactory();
    }

    self.internalSrc
      .then(function (pdfDocument) {
        // Document loaded, retrieving the page.
        self.pdf = pdfDocument;
        return pdfDocument.getPage(self.page);
      })
      .then(function (pdfPage) {
        // Creating the page view with default parameters.
        self.pdfViewer = new PDFPageView({
          container: container,
          id: self.page,
          scale: 1,
          defaultViewport: pdfPage.getViewport({
            scale: 1,
          }),
          eventBus: eventBus,
          textLayerFactory: textLayer,
        });
        // Associates the actual page with the view, and drawing it
        self.pdfViewer.setPdfPage(pdfPage);
        // Set up a scrollPageIntoView function for our links
        var viewer = {
          scrollPageIntoView: function (params) {
            // Send an event when clicking internal links so we can handle loading/scrolling to the correct page
            self.$emit('link-clicked', params);
          },
        };
        self.pdfLinkService.setDocument(self.pdf);
        self.pdfLinkService.setViewer(viewer);
        self.pdfFindController.setDocument(self.pdf);
        self.drawScaled(self.scale);

        self.loading = false;
        self.$emit('loading', false);
      })
      .catch((err) => {
        self.$emit('error', err);
        self.loading = false;
        self.$emit('loading', false);
      });
  },
  beforeUnmount() {
    var self = this;
    if (self.pdfViewer) {
      self.pdfViewer.destroy();
      self.pdfViewer = null;
    }
  },
  methods: {
    async setCanvasSize() {
      // we need to set also the total scale factor manually as it is not set for all documents
      const viewerContainer = this.$refs?.container;
      if (viewerContainer) {
        const computedStyle = window.getComputedStyle(viewerContainer);
        const scaleFactor = computedStyle.getPropertyValue('--scale-factor');
        viewerContainer.style.setProperty('--total-scale-factor', scaleFactor);
      }

      let devicePixelRatio = window.devicePixelRatio || 1;
      if (devicePixelRatio === 1) {
        return;
      }

      let canvas = null;
      let checkLimits = 0;
      while (!canvas && checkLimits < 50) {
        canvas = this.$refs?.container?.querySelector('canvas');
        if (canvas) {
          canvas.style.maxWidth = `${this.pdfViewer?.viewport?.width}px`;
          canvas.style.maxHeight = `${this.pdfViewer?.viewport?.height}px`;
        }
        checkLimits++;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    },
    drawScaled: function (newScale) {
      if (this.pdfViewer) {
        setTimeout(async () => {
          await this.setCanvasSize(newScale);
        }, 50);
        this.pdfViewer.update({
          scale: newScale,
          rotation: this.rotate,
        });
        this.pdfViewer.draw();
        // The findController needs the text layer to have been created in the Draw() function, so link it in now
        if (this.text) {
          this.pdfViewer.textLayer.findController = this.pdfFindController;
        }
        this.loading = false;
        this.$emit('loading', false);
      }
    },
  },
};
</script>
<style src="pdfjs-dist/web/pdf_viewer.css"></style>
<style lang="scss">
:root {
  --scale-round-x: 0.05px;
  --scale-round-y: 0.05px;
}
// stop links being highlighted when annotations are enabled
.textLayer .highlight {
  background-color: transparent;
}
// hide links
.annotationLayer a {
  display: none;
}
// disallow fields from being editable
.annotationLayer input,
.annotationLayer select {
  pointer-events: none;
}

.page-loading {
  min-width: 612px;
  width: 100%;
  height: 792px;
}
</style>
